import { decodeConfig } from '../utils/firebaseConfigDecoder';

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

interface EncryptedFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

/**
 * Encrypted Firebase configurations for each LOB
 * Generated using the encodeConfig utility (scripts/encodeFirebaseConfigs.js)
 */
const encryptedConfigs: { [lob: string]: EncryptedFirebaseConfig } = {
  'default': {
    apiKey: '71745c066f797e0571620e5e072b7e3379025a7065795c795675370407197e3475785b034b78094e60784c0407377e1c71796b57',
    authDomain: '6f0242424b5d055c4a76165c070d6f107e7a77427d7671546f5d0e443e346b0c4c0279424c626a5470025b40',
    projectId: '6f0242424b5d055c4a76165c070d6f107e7a77427d76710d',
    storageBucket: '6f0242424b5d055c4a76165c070d6f107e7a77427d7671546f5d0e443e346b0c4c0279505a76094f70750642280e7e134c6a0d0d',
    messagingSenderId: '7e4d74007d726a017d50370429274c1c',
    appId: '7d725f027e7271497e7a37072a376f137f7278065a02794e7c5d27012a346a5a7f7a425c6f50564f7e7201013e095a087d4d71496f75740d',
    measurementId: '774f0174774d6d6e7e74154537256a43'
  },

  'ckcoeuat': {
    apiKey: '71745c066f797e057775385e05345a176f505c72587a6372725b067c073b70224a63405e4c00524a75005659010f483479027750',
    authDomain: '6f0263024a75055770692842283b660c5a6c055d4a696d5c705d24463e347e134c6c054d4b02000d',
    projectId: '6f0263024a75055770692842283b660c5a6a0d0d',
    storageBucket: '6f0263024a75055770692842283b660c5a6c055d4a696d5c705d24463e3b635a4b036d427002745470692045',
    messagingSenderId: '7e4d74507e7271497e4d2b012b375f58',
    appId: '7d725f027e727d017d7a274429094c4a7f7a74065a02794e7c4d2b0429376f1e7d4d774e7e4d5b507c7a37012a247e08707a7d0370726a0d',
    measurementId: '774f004f795c6b72607a346e36356e43'
  },

  'ckcoedemo': {
    apiKey: '71745c066f797e05775d0e42350e665a720142637c750504725e2c5c374c09284a506b00715c5c5d6f4d307c304a7e2d7d784504',
    authDomain: '6f0263024a75055770692842283470084b750854705d5c4f6f75285c074a660c4c626a546f020952',
    projectId: '6f0263024a75055770692842283470084b75080d',
    storageBucket: '6f0263024a75055770692842283470084b750854705d5c4f6f75285c074a661e5a76094f6f755a5c7f5d24450722054e',
    messagingSenderId: '7d726a507d726a037d722b442a37641c7d710d0d',
    appId: '7d725f497d7a7d497e7a010529096a587e7278497f5e5a5c704d125c29277e097f766d4e7e506a047d752f043e246a5a7d757d027d7a564f',
    measurementId: '774f01747f72707e715b050432277028'
  },

  'ckcoe': {
    apiKey: '71745c066f797e0577754b040648662d4a4d74047e627d04744e4c65374c5a176502406975626d6e7c7a09042a486b0b72750455',
    authDomain: '6f0245524c626d596f6c4c5c2b247d5a7d6c055d4a696d5c705d24463e347e134c6c054d4b02000d',
    projectId: '6f0245524c626d596f6c4c5c2b247d5a7d6a0d0d',
    storageBucket: '6f0245524c626d596f6c4c5c2b247d5a7d6c055d4a696d5c705d24463e3b635a4b036d427002745470692045',
    messagingSenderId: '7d724c007d505b497c7223072a376e5a',
    appId: '7d725f497e5071507c72270129275b587d7a71065a02794e7c4d0a5d29095b4a6f72775b7e506e007c723b04292777097e4d78037e50740d',
    measurementId: '774f00047e7a5a747e7a2c6429084036'
  }
};

/**
 * Decrypts and returns the Firebase configuration for a given LOB
 */
export function getFirebaseConfig(lob: string): FirebaseConfig {
  // Normalize LOB identifier
  const normalizedLob = lob?.toLowerCase().trim() || 'default';
  
  // Get encrypted config for the LOB, fallback to default
  const encryptedConfig = encryptedConfigs[normalizedLob] || encryptedConfigs['default'];
  
  if (!encryptedConfig) {
    console.error(`Firebase config not found for LOB: ${normalizedLob}, using default`);
    throw new Error('Firebase configuration not available');
  }
  
  try {
    // Decrypt each field
    const config: FirebaseConfig = {
      apiKey: decodeConfig(encryptedConfig.apiKey),
      authDomain: decodeConfig(encryptedConfig.authDomain),
      projectId: decodeConfig(encryptedConfig.projectId),
      storageBucket: decodeConfig(encryptedConfig.storageBucket),
      messagingSenderId: decodeConfig(encryptedConfig.messagingSenderId),
      appId: decodeConfig(encryptedConfig.appId),
    };
    
    // Add optional measurementId if present
    if (encryptedConfig.measurementId) {
      config.measurementId = decodeConfig(encryptedConfig.measurementId);
    }
    
    return config;
  } catch (error) {
    console.error(`Failed to decrypt Firebase config for LOB: ${normalizedLob}`, error);
    throw new Error('Failed to load Firebase configuration');
  }
}

/**
 * Checks if a Firebase configuration exists for a given LOB
 */
export function hasFirebaseConfig(lob: string): boolean {
  const normalizedLob = lob?.toLowerCase().trim();
  return normalizedLob in encryptedConfigs;
}
